<template>
  <div class="edit-tags-compact form-group">
    <label class="form-label">Tags</label>
    <div class="tags-container">
      <!-- Selected tags as chips -->
      <span
        v-for="tag in selectedTagsWithLabels"
        :key="tag.id"
        class="tag-chip">
        {{ tag.label }}
        <span class="tag-remove" @click="removeTag(tag.id)">&times;</span>
      </span>

      <!-- Add button with dropdown -->
      <b-dropdown
        v-model="dropdownOpen"
        aria-role="menu"
        position="is-bottom-left"
        :scrollable="true"
        :max-height="350"
        :close-on-click="false"
        trap-focus
        append-to-body
        @active-change="onDropdownActive">
        <template #trigger>
          <button class="add-tag-button" type="button">
            <b-icon icon="plus" size="is-small" />
            <span>Add</span>
          </button>
        </template>

        <!-- Search filter -->
        <div class="dropdown-search px-3 py-2">
          <b-input
            ref="searchInput"
            v-model="searchQuery"
            placeholder="Search tags..."
            size="is-small"
            icon="magnify"
            @input="onSearch"
          />
        </div>

        <template v-if="searchQuery">
          <!-- Flat search results -->
          <b-dropdown-item
            v-for="tag in filteredTags"
            :key="tag.id"
            custom
            :class="{ 'is-disabled': isTagSelected(tag.id) }">
            <div
              class="dropdown-tag-item is-flex is-justify-content-space-between"
              @click="!isTagSelected(tag.id) && addTag(tag.id)">
              <span>{{ tag.label }}</span>
              <span class="has-text-grey is-size-7">{{ tag.categoryLabel }}</span>
            </div>
          </b-dropdown-item>
          <b-dropdown-item v-if="filteredTags.length === 0" custom>
            <span class="has-text-grey">No matching tags</span>
          </b-dropdown-item>
        </template>
        <template v-else>
          <!-- Grouped by category -->
          <template v-for="category in filteredCategories">
            <b-dropdown-item :key="category.id + '_header'" custom class="dropdown-category-header">
              <strong>{{ category.label }}</strong>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="tag in category.tags"
              :key="tag.id"
              custom
              :class="{ 'is-disabled': isTagSelected(tag.id) }">
              <div
                class="dropdown-tag-item"
                @click="!isTagSelected(tag.id) && addTag(tag.id)">
                <span :class="{ 'has-text-grey': isTagSelected(tag.id) }">
                  {{ tag.label }}
                  <b-icon v-if="isTagSelected(tag.id)" icon="check" size="is-small" class="ml-1" />
                </span>
              </div>
            </b-dropdown-item>
          </template>
        </template>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
import db from '@/firebase/db'
import { TAG_CATEGORIES } from '@/data/tagCategories'

export default {
  name: 'EditTags',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    entityType: {
      type: String,
      default: 'recipes'
    }
  },
  data() {
    return {
      dropdownOpen: false,
      searchQuery: '',
      categories: []
    }
  },
  created() {
    this.loadCategories()
  },
  computed: {
    selectedTags: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    filteredCategories() {
      return this.categories
        .filter(cat => cat.applies_to.includes(this.entityType))
        .sort((a, b) => a.order - b.order)
    },
    allTags() {
      const tagMap = {}
      this.categories.forEach(category => {
        if (category.applies_to.includes(this.entityType)) {
          category.tags.forEach(tag => {
            tagMap[tag.id] = {
              ...tag,
              categoryId: category.id,
              categoryLabel: category.label
            }
          })
        }
      })
      return tagMap
    },
    allTagsArray() {
      return Object.values(this.allTags)
    },
    selectedTagsWithLabels() {
      return this.selectedTags
        .map(tagId => this.allTags[tagId])
        .filter(tag => tag != null)
    },
    filteredTags() {
      if (!this.searchQuery) return []
      const query = this.searchQuery.toLowerCase()
      return this.allTagsArray.filter(tag =>
        tag.label.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    onDropdownActive(active) {
      if (active) {
        setTimeout(() => {
          const input = this.$refs.searchInput
          if (input && input.$el) {
            const el = input.$el.querySelector('input')
            if (el) el.focus()
          }
        }, 50)
      } else {
        this.searchQuery = ''
      }
    },
    async loadCategories() {
      try {
        const snapshot = await db.collection('tag_categories').orderBy('order').get()
        if (snapshot.empty) {
          // Fallback to static file if Firestore is empty
          this.categories = Object.values(TAG_CATEGORIES)
        } else {
          this.categories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        }
      } catch (error) {
        console.error('Error loading tag categories:', error)
        this.categories = Object.values(TAG_CATEGORIES)
      }
    },
    isTagSelected(tagId) {
      return this.selectedTags.includes(tagId)
    },
    addTag(tagId) {
      if (!this.isTagSelected(tagId)) {
        this.$emit('input', [...this.selectedTags, tagId])
      }
      this.searchQuery = ''
      this.$nextTick(() => {
        const input = this.$refs.searchInput
        if (input && input.$el) {
          const el = input.$el.querySelector('input')
          if (el) el.focus()
        }
      })
    },
    removeTag(tagId) {
      this.$emit('input', this.selectedTags.filter(t => t !== tagId))
    },
    onSearch() {
      // Keep dropdown open during search
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 22px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--feeds-green-light);
  color: var(--feeds-green);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.tag-chip:hover {
  background: #d0ece9;
}

.tag-remove {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
  font-size: 16px;
  line-height: 1;
}

.tag-remove:hover {
  opacity: 1;
}

.add-tag-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px dashed var(--border-medium);
  border-radius: 20px;
  background: transparent;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-tag-button:hover {
  border-color: var(--feeds-green);
  color: var(--feeds-green);
  background: var(--feeds-green-light);
}

.dropdown-search {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-bottom: 1px solid #f5f5f5;
}

.dropdown-tag-item {
  cursor: pointer;
  padding: 0.375rem 1rem;
  margin: 0 -1rem;
}

.dropdown-tag-item:hover {
  background: #f5f5f5;
}

.is-disabled .dropdown-tag-item {
  cursor: default;
  opacity: 0.5;
}

.is-disabled .dropdown-tag-item:hover {
  background: transparent;
}

.dropdown-category-header {
  background-color: #f5f5f5;
  padding: 0.5rem 1rem !important;
  cursor: default;
}

.dropdown-category-header:hover {
  background-color: #f5f5f5 !important;
}
</style>
